import { useMemo, useState } from "react";
import { useAdminTiers, type PricingUpsert } from "../../hooks/useAdminTiers";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { TierFormModal } from "../../components/admin/tiers/TierFormModal";
import {
  BASE_PLAN_ITEM_CODE,
  BILLING_CURRENCIES,
  BILLING_INTERVALS,
  billingItemLabel,
  currencyLabel,
  formatInterval,
  isPlanItem,
  isSetupFeeItem,
  ONCE_INTERVAL,
  planItemCodeFor,
  SETUP_FEE_ITEM_CODE,
  setupFeeItemCodeFor,
  type MutationResult,
  type Tier,
} from "../../types/billing";
import styles from "./AdminTiers.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

const TIER_HEADERS = ["Tier", "Rank", "Slots", "Coaching", "Trial days", "Status", ""];

// Admin "Tiers & Pricing" tab: CRUD on tier_definition + an inline-editable
// per-tier price grid (tier_pricing). Behind the billing_stripe flag.
export const AdminTiers = (): JSX.Element => {
  const { status, tiers, error, isFetching, createTier, updateTier, deleteTier, setPricing } = useAdminTiers();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Tier | null>(null);
  const [deleting, setDeleting] = useState<Tier | null>(null);
  // The price grid shows one currency at a time; the toggle switches it. Each
  // currency is priced independently (a tier_pricing row per currency).
  const [currency, setCurrency] = useState<string>(BILLING_CURRENCIES[0]);

  // Pricing-grid rows are the tiers (by rank, as returned); columns are the
  // union of (item, interval) pairs priced across tiers (in any currency, so the
  // grid shape is identical between currencies even before the other currency's
  // prices are entered), base plan first and then by interval (monthly before
  // yearly). Per-tier plan items (base-plan-<tier>) collapse into ONE "Base
  // Plan" column; each tier's cell in it reads/writes that tier's own item. The
  // one-time setup-fee column is always offered so it can be priced even when no
  // row exists yet.
  const pricingRows = useMemo(() => {
    const map = new Map<string, { itemCode: string; interval: string }>();
    tiers.forEach((t) =>
      t.pricing.forEach((p) => {
        const code = isPlanItem(p.itemCode)
          ? BASE_PLAN_ITEM_CODE
          : isSetupFeeItem(p.itemCode)
            ? SETUP_FEE_ITEM_CODE
            : p.itemCode;
        map.set(`${code}:${p.interval}`, { itemCode: code, interval: p.interval });
      }),
    );
    map.set(`${SETUP_FEE_ITEM_CODE}:${ONCE_INTERVAL}`, { itemCode: SETUP_FEE_ITEM_CODE, interval: ONCE_INTERVAL });
    const intervalRank = (iv: string): number => {
      const i = (BILLING_INTERVALS as readonly string[]).indexOf(iv);
      return i === -1 ? BILLING_INTERVALS.length : i;
    };
    return Array.from(map.values()).sort((a, b) => {
      if (a.itemCode !== b.itemCode) {
        if (a.itemCode === BASE_PLAN_ITEM_CODE) return -1;
        if (b.itemCode === BASE_PLAN_ITEM_CODE) return 1;
        return a.itemCode.localeCompare(b.itemCode);
      }
      return intervalRank(a.interval) - intervalRank(b.interval);
    });
  }, [tiers]);

  // Tiers are rows and items are columns, so a new tier grows the grid
  // downward instead of pushing it off the page. Each item spans its intervals.
  const pricingGroups = useMemo(() => {
    const groups: Array<{ itemCode: string; intervals: string[] }> = [];
    for (const { itemCode, interval } of pricingRows) {
      const last = groups[groups.length - 1];
      if (last !== undefined && last.itemCode === itemCode) last.intervals.push(interval);
      else groups.push({ itemCode, intervals: [interval] });
    }
    return groups;
  }, [pricingRows]);

  return (
    <div className={styles.page}>
      <AdminPageIntro title={<>Tiers &amp; pricing</>}>
        Each tier sets its included integration slots and coaching sessions; the price grid sets what each catalog item costs at that tier.
      </AdminPageIntro>

      {status === "loading" && <AdminTableSkeleton headers={TIER_HEADERS} tableClassName={styles.table ?? ""} />}
      {status === "error" && (
        <div className={styles.errorPanel}>Couldn&apos;t load tiers{error !== null ? `: ${error}` : "."}</div>
      )}

      {status === "ready" && (
        <>
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h3 className={styles.sectionTitle}>Tiers</h3>
              <button type="button" className={styles.primary} onClick={() => setCreating(true)}>
                New tier
              </button>
            </div>

            <div className={styles.tableWrap}>
              <table className={isFetching ? `${styles.table} ${styles.tableFetching}` : styles.table}>
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th className={styles.num}>Rank</th>
                    <th className={styles.num}>Slots</th>
                    <th className={styles.num}>Coaching</th>
                    <th className={styles.num}>Trial days</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t) => (
                    <tr key={t.tierCode}>
                      <td>
                        <span className={styles.tierName}>{t.displayName}</span>
                        <code className={styles.tierCode}>{t.tierCode}</code>
                      </td>
                      <td className={styles.num}>{t.rank}</td>
                      <td className={styles.num}>{t.includedSlots}</td>
                      <td className={styles.num}>{t.includedCoachingSessions}</td>
                      <td className={styles.num}>{t.trialDays ?? 0}</td>
                      <td>
                        <span className={t.active ? styles.badgeActive : styles.badgeInactive}>
                          {t.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className={styles.rowActions}>
                        <button type="button" className={styles.linkButton} onClick={() => setEditing(t)}>
                          Edit
                        </button>
                        <button type="button" className={styles.linkButtonDanger} onClick={() => setDeleting(t)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {tiers.length === 0 && (
                    <tr>
                      <td colSpan={7} className={styles.empty}>
                        No tiers yet. Create one to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {tiers.length > 0 && pricingRows.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <h3 className={styles.sectionTitle}>Pricing</h3>
                <div className={styles.pricingControls}>
                  <span className={styles.currencyNotice}>Set each currency separately</span>
                  <div className={styles.currencyToggle} role="group" aria-label="Pricing currency">
                    {BILLING_CURRENCIES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={c === currency ? styles.currencyOptionActive : styles.currencyOption}
                        aria-pressed={c === currency}
                        onClick={() => setCurrency(c)}
                      >
                        {currencyLabel(c)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.tableWrap}>
                <table
                  className={
                    isFetching
                      ? `${styles.table} ${styles.priceGrid} ${styles.tableFetching}`
                      : `${styles.table} ${styles.priceGrid}`
                  }
                >
                  <thead>
                    <tr>
                      <th rowSpan={2}>Tier</th>
                      {pricingGroups.map((g) => (
                        <th key={g.itemCode} colSpan={g.intervals.length} className={`${styles.groupHead} ${styles.groupStart}`}>
                          {billingItemLabel(g.itemCode)}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {pricingGroups.map((g) =>
                        g.intervals.map((interval, i) => (
                          <th
                            key={`${g.itemCode}:${interval}`}
                            className={i === 0 ? `${styles.intervalHead} ${styles.groupStart}` : styles.intervalHead}
                          >
                            {formatInterval(interval)}
                          </th>
                        )),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.map((t) => (
                      <tr key={t.tierCode}>
                        <td className={styles.priceTier}>
                          <span className={styles.priceTierName} title={`${t.displayName} (${t.tierCode})`}>
                            {t.displayName}
                          </span>
                        </td>
                        {pricingGroups.map((g) =>
                          g.intervals.map((interval, i) => {
                            // The normalized "Base Plan" / "Setup Fee" columns map
                            // to this tier's own items in its row.
                            const cellItemCode =
                              g.itemCode === BASE_PLAN_ITEM_CODE
                                ? planItemCodeFor(t.tierCode)
                                : g.itemCode === SETUP_FEE_ITEM_CODE
                                  ? setupFeeItemCodeFor(t.tierCode)
                                  : g.itemCode;
                            const p = t.pricing.find(
                              (x) => x.itemCode === cellItemCode && x.interval === interval && x.currency === currency,
                            );
                            return (
                              <td
                                key={`${g.itemCode}:${interval}`}
                                className={i === 0 ? `${styles.priceCell} ${styles.groupStart}` : styles.priceCell}
                              >
                                <PricingCell
                                  key={`${currency}:${p?.unitAmountCents ?? 0}:${p?.included ?? false}`}
                                  tierCode={t.tierCode}
                                  itemCode={cellItemCode}
                                  interval={interval}
                                  cents={p?.unitAmountCents ?? 0}
                                  currency={currency}
                                  included={p?.included ?? false}
                                  onSave={setPricing}
                                />
                              </td>
                            );
                          }),
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {creating && (
        <TierFormModal mode="create" onSubmit={(b) => createTier(b)} onClose={() => setCreating(false)} />
      )}
      {editing !== null && (
        <TierFormModal
          mode="edit"
          initial={editing}
          onSubmit={(b) => updateTier(editing.tierCode, b)}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting !== null && (
        <DeleteTierModal
          tier={deleting}
          onConfirm={() => deleteTier(deleting.tierCode)}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
};

type PricingCellProps = {
  readonly tierCode: string;
  readonly itemCode: string;
  readonly interval: string;
  readonly cents: number;
  readonly currency: string;
  readonly included: boolean;
  readonly onSave: (
    tierCode: string,
    itemCode: string,
    interval: string,
    body: PricingUpsert,
  ) => Promise<MutationResult>;
};

// One editable price-book cell: amount saves on blur (when changed). Re-mounted
// by the parent when the server value changes (via key), so it stays in sync
// after a reload.
const PricingCell = ({
  tierCode,
  itemCode,
  interval,
  cents,
  currency,
  included,
  onSave,
}: PricingCellProps): JSX.Element => {
  const [amount, setAmount] = useState((cents / 100).toFixed(2));
  const [state, setState] = useState<"idle" | "saving" | "error">("idle");
  // A seeded-but-unedited price is $0. Flag it so admins can see what still
  // needs a real price (a $0 row charges $0 -- prices must be set before
  // billing is enabled for real customers).
  const unset = cents === 0;

  const onAmountBlur = async (): Promise<void> => {
    const normalized = Math.max(0, Math.round((parseFloat(amount) || 0) * 100));
    setAmount((normalized / 100).toFixed(2));
    if (normalized === cents) return;
    setState("saving");
    // `included` is preserved as-is: the price book carries it, but it isn't
    // surfaced in the UI yet (nothing consumes it until pricing is wired into
    // purchases). Passing the current value avoids resetting it on a price edit.
    const result = await onSave(tierCode, itemCode, interval, { unitAmountCents: normalized, currency, included });
    setState(result.ok ? "idle" : "error");
  };

  return (
    <div className={styles.cell}>
      <div
        className={unset ? `${styles.amountWrap} ${styles.amountUnset}` : styles.amountWrap}
        title={unset ? "No price set yet" : undefined}
      >
        <span className={styles.currency}>$</span>
        <input
          className={styles.priceInput}
          type="number"
          min={0}
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onBlur={onAmountBlur}
          aria-label={`${itemCode} ${interval} price for ${tierCode}`}
        />
      </div>
      {state === "saving" && <span className={styles.cellSaving} aria-hidden>…</span>}
      {state === "error" && (
        <span className={styles.cellError} title="save failed" aria-label="save failed">
          !
        </span>
      )}
    </div>
  );
};

type DeleteTierModalProps = {
  readonly tier: Tier;
  readonly onConfirm: () => Promise<MutationResult>;
  readonly onClose: () => void;
};

const DeleteTierModal = ({ tier, onConfirm, onClose }: DeleteTierModalProps): JSX.Element => {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = await onConfirm();
    setBusy(false);
    if (result.ok) onClose();
    else setError(result.message);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.confirmDialog}
        role="dialog"
        aria-modal="true"
        aria-label="Delete tier"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={styles.confirmTitle}>Delete {tier.displayName}?</h3>
        <p className={styles.confirmBody}>
          This removes the tier and its pricing. Tenants on this tier must be reassigned first.
        </p>
        {error !== null && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.dangerButton} disabled={busy} onClick={confirm}>
            {busy ? "Deleting…" : "Delete tier"}
          </button>
        </div>
      </div>
    </div>
  );
};
